/**
 * Customizes our Backbone setup
 */

import './backbone-extensions'
import Backbone from 'backbone'

import errors from 'scoreboard/components/setup-errors'
import _ from 'lodash'
import ErrorView from 'scoreboard/components/error-view'
import UnsavedWarning from 'scoreboard/components/unsaved/unsaved-warning'
import appDisplay from 'scoreboard/components/app-display'
import dialogStack from 'scoreboard/components/dialog-stack'
import {isOffline} from 'scoreboard/components/xhr'
import FormView from 'scoreboard/components/form-view'

/**
 * Handles unauthenticated users first, then defers to existing error handler.
 * If none was provided, an error dialog is displayed.
 *
 * @param {Object} [originalOptions.error] An existing Backbone error handler
 */
function addErrorHandler(originalOptions = {}) {
	var error = originalOptions.error
	// Backbone messes with the original options so we have to be careful
	// what we pass around
	originalOptions.error = function(model, xhr, options) {
		const {status, responseJSON} = xhr

		// handle users not being logged in
		if (errors.expiredLoginHandler(xhr)) {
			return
		}

		// after that, give precedence for any error handler that was explicitly
		// specified
		if (error) {
			error.apply(this, arguments)
			return
		}

		// #31389 Moved below the above statement so specific scenarios can be
		// handled. We have already displayed an offline message, so we don't need
		// to allow the fallback below to execute
		if (isOffline(xhr)) {
			return
		}

		// fallback to just throwing up a dialog with a stack trace
		if (responseJSON) {
			appDisplay.replaceDialog(new ErrorView(errors.errorViewOptions(status, responseJSON)))
		}
	}
	// add a flag to the function so we know it was a default error handler
	if (error) {
		error.default = true
	}
}

function setupBackbone() {

	function removeView(parentView, view) {
		if (view?.$el && !view.__removed) {
			view.__removed = true
			view.remove()
		}
	}

	// this is very noisy debugging for day to day development
	let debug = false
	const viewRemove = Backbone.View.prototype.remove
	Backbone.View.prototype.remove = function() {
		this.trigger('remove')
		// #51127 we can't auto remove globally because it breaks some views
		if (this.autoRemove != true) {
			viewRemove.apply(this, arguments)
			return
		}

		_.forIn(this, (value, key) => {
			if (value instanceof Backbone.View) {
				if (debug) {
					console.log(`key: ${key}, value: ${value}`)
				}
				removeView(this, value)
			}
			if (_.isArray(value)) {
				if (debug) {
					console.log(`examining subcomponents for key ${key}`)
				}
				_.each(value, (value, index) => {
					if (value instanceof Backbone.View) {
						if (debug) {
							console.log(`key: ${key}, index: ${index}, value: ${value}`)
						}
						removeView(this, value)
					}
				})
			}
		})
		// #51090 sub-views could have references back to a parent FormView,
		// which we don't want to remove from the DOM
		if (this instanceof FormView) {
			this.stopListening()
		} else {
			return viewRemove.apply(this, arguments)
		}
	}

	/**
	 * Clears all the attributes in the model and resets the model so it has the
	 * given attributes instead
	 */
	Backbone.Model.prototype.reset = function(attributes) {
		attributes = _.clone(attributes.attributes || attributes)

		var unset = _.difference(_.keys(this.attributes), _.keys(attributes))
		_.each(unset, function(key) {
			this.unset(key)
		}.bind(this))

		this.set(attributes)
	}

	Backbone.Model.prototype.cloneDeep = function() {
		return new this.constructor(_.cloneDeep(this.attributes))
	}

	// Note: this code doesn't look very DRY, but you can't override
	// Backbone.sync directly because by the time Backbone.sync is called a
	// default error handler was already provided by Backbone, so we can't tell
	// if it's appropriate to add our default error handler instead

	var collectionFetch = Backbone.Collection.prototype.fetch
	Backbone.Collection.prototype.fetch = function(options) {
		addErrorHandler.call(this, options)
		return collectionFetch.call(this, options)
	}

	var fetch = Backbone.Model.prototype.fetch
	Backbone.Model.prototype.fetch = function(options = {}) {
		addErrorHandler.call(this, options)
		return fetch.call(this, options)
	}

	var destroy = Backbone.Model.prototype.destroy
	Backbone.Model.prototype.destroy = function(options = {}) {
		addErrorHandler.call(this, options)
		return destroy.call(this, options)
	}

	var save = Backbone.Model.prototype.save
	Backbone.Model.prototype.save = function(key, val, options = {}) {
		// Handle both `"key", value` and `{key: value}` -style arguments. (copied from backbone source)
		var attrs
		if (key == null || typeof key === 'object') {
			attrs = key
			options = val
		} else {
			(attrs = {})[key] = val
		}

		addErrorHandler.call(this, options)
		return save.call(this, key, val, options)
	}

	// based on http://stackoverflow.com/a/31192723/393274
	// adapted from https://github.com/STRML/backbone.routeNotFound/blob/master/backbone.routeNotFound.js
	var loadUrl = Backbone.History.prototype.loadUrl
	Backbone.History.prototype.loadUrl = function(fragment, options) {
		var matched = loadUrl.apply(this, arguments)
		// issue #6590 if no route found, go to welcome screen
		if (!matched && !_.get(options, 'preventWelcomeRedirect')) {
			(new Backbone.Router()).navigate('home/welcome', { trigger: true, replace: true })
			return true
		}
		return matched
	}

	/**
	 * Backbone sourcecode comment:
	 * Checks the current URL to see if it has changed, and if it has,
	 * calls `loadUrl`, normalizing across the hidden iframe.
	 *
	 * We override this in order to handle navigation when dialogs are open
	 */
	// Note: this History constructor binds this.checkUrl so we can't assign to the Prototype
	// https://github.com/jashkenas/backbone/blob/master/backbone.js#L1747
	const checkUrl = Backbone.history.checkUrl
	Backbone.history.checkUrl = e => {
		const dialog = dialogStack.peek()
		if (canRemoveDialog(dialog)) {
			dialogStack.remove(dialog)
			return checkUrl.call(this, arguments)
		} else {
			const history = Backbone.history
			// re-push the current page into the history (at this stage it's been popped)
			// Can't use Backbone.history because it short-circuits when the current url == fragment
			window.history.pushState({}, document.title, history.root + history.fragment)
			return false
		}
	}

	let navigate = Backbone.History.prototype.navigate
	Backbone.History.prototype.navigate = function(fragment, options = {}) {
		if (!options.silent) {
			Backbone.trigger('history:before-navigate')
		}
		navigate.apply(this, arguments)
	}
}

const nonDismissableViews = [UnsavedWarning]
/**
 * This gets called on every nav, so it short circuits at each condition.
 *
 * 1.) prevent forward/backward navigation while a form dialog is open.
 * 		Because if the view underneath a dialog changes,
 * 		the form changes can not be submitted as a result of a guard.
 *
 * 2.) prevent navigation if a guard is open; user must deal with the guard first
 *
 * 3.) If any other dialog (ErrorView, non-form) is open close it and navigate where the user requested. (#17809)
 *
 * @param {Backbone.View} [dialog]
 * @return {boolean} True if there is an active dialog and it can be dismissed
 * when user navigates via browser
 */
function canRemoveDialog(dialog) {
	if (_.isNil(dialog)) {
		return true
	}
	const dialogView = _.get(dialog, 'options.content')

	const nonDismissableView = _.some(nonDismissableViews, v => (dialogView instanceof v))
	if (nonDismissableView) {
		return false
	}

	const hasForm = dialog.$('form').length > 0
	if (!hasForm) {
		return true
	}

	return false
}

export default setupBackbone

setupBackbone.__filename__ = 'setup-backbone.js'
